import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  effect,
  input,
  linkedSignal,
  untracked,
} from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { FilterService } from '@progress/kendo-angular-grid';

import {
  CheckBoxFilterMenuBase,
  CheckBoxFilterMenuOptions,
} from './grid-checkbox-filter-menu.base';

/**
 * Signal-input OnPush sibling of CheckBoxFilterMenuComponent (grid-checkbox-filter-menu),
 * built for WI 31712 (zoneless readiness). Same behavior and template; since WI 35564 both
 * menus are OnPush and share CheckBoxFilterMenuBase, so this sibling differs only in taking
 * its inputs as `input()` signals. Both remain in the barrel until every consumer is cut
 * over to one of them.
 */
@Component({
  selector: 'grid-checkbox-filter-menu-signal',
  templateUrl: 'grid-checkbox-filter-menu.component.html',
  styleUrls: ['./grid-checkbox-filter-menu.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckBoxFilterMenuSignalComponent extends CheckBoxFilterMenuBase {
  public readonly height = input(200);
  public readonly isLoadingFilterData = input(false);

  public readonly isPrimitive = input<boolean>(undefined);
  public readonly textField = input<string>(undefined);
  public readonly valueField = input<string>(undefined);
  /** Optional item field shown as each option's hover title (parity with the original). */
  public readonly hintField = input<string>(undefined);

  public readonly currentFilter = input<CompositeFilterDescriptor>(undefined);
  public readonly filterService = input<FilterService>(undefined);

  public readonly field = input<string>(undefined);
  // NOTE: originally it was contains, but it has to be eq for checkbox selection!
  public readonly operator = input('eq');

  public readonly compositeLogic = input<'or' | 'and'>('or');

  public readonly collectionField = input<string>(undefined);
  public readonly collectionRef = input<string>(undefined);
  public readonly collectionItemField = input<string>(undefined);
  public readonly collectionOperator = input<'any' | 'all'>('any');

  public readonly isSingleSelection = input(false);
  public readonly alwaysShowSelectedItems = input(true); // NOTE: should be true by def.
  public readonly keepExtraFilterValues = input(false);

  public readonly showFilter = input(true);
  public readonly showFilterOperators = input(false);

  public readonly addSelectAllOption = input(false);

  public readonly addBlankOption = input(false);
  public readonly customBlankOptionText = input<string>(undefined);

  // Input default that is also changed internally by the operator dropdown.
  // (Binding names are unchanged; the aliased fields were dropped for no-input-rename.)
  public readonly dataItemSearchOperator = input('contains');
  protected readonly searchOperator = linkedSignal(() =>
    this.dataItemSearchOperator()
  );

  public readonly isGuidValue = input(false);

  /** raw incoming items; the rendered list (with the Blank option) is `menuData` */
  public readonly data = input<unknown[]>(undefined);
  protected readonly rawItems = computed(() => this.data() ?? []);

  /** items that are shown in the checkbox filter, after the search box filter */
  public readonly filteredDataItems = this.visibleItems;
  public readonly dataItemFilterText = this.searchText;

  /** selected item's value fields */
  public readonly value = this.selected;

  /** NOT USED ANYWHERE */
  @Output() public valueChange = new EventEmitter<number[]>();

  constructor() {
    super();
    // Parse checked values out of the assigned filter once it is available. Seeds only
    // once: later currentFilter re-emits (from our own filterService().filter() calls)
    // must not re-concat, or the selection accumulates duplicates.
    effect(() => {
      const filter = this.currentFilter();
      untracked(() => this.seedFromFilter(filter));
    });
  }

  protected opts(): CheckBoxFilterMenuOptions {
    return {
      height: this.height(),
      isLoadingFilterData: this.isLoadingFilterData(),
      isPrimitive: this.isPrimitive(),
      textField: this.textField(),
      valueField: this.valueField(),
      hintField: this.hintField(),
      isSingleSelection: this.isSingleSelection(),
      alwaysShowSelectedItems: this.alwaysShowSelectedItems(),
      showFilter: this.showFilter(),
      showFilterOperators: this.showFilterOperators(),
      addSelectAllOption: this.addSelectAllOption(),
      addBlankOption: this.addBlankOption(),
      customBlankOptionText: this.customBlankOptionText(),
      // Read only by the event handlers, so a computed must not re-derive on them (a
      // currentFilter re-emit would otherwise re-filter the visible list).
      ...untracked(() => ({
        currentFilter: this.currentFilter(),
        filterService: this.filterService(),
        field: this.field(),
        operator: this.operator(),
        compositeLogic: this.compositeLogic(),
        collectionField: this.collectionField(),
        collectionRef: this.collectionRef(),
        collectionItemField: this.collectionItemField(),
        collectionOperator: this.collectionOperator(),
        keepExtraFilterValues: this.keepExtraFilterValues(),
        isGuidValue: this.isGuidValue(),
      })),
    };
  }
}
